import { useContext } from "react";
import type { CSSProperties } from "react";
import { Button, createDateFormatter, useDisplayTimezone } from "@nexus/ui";
import { WidgetDataIssueContext } from "../contexts/WidgetFeaturesContext";
import { WIDGET_ISSUE_TEXT, toDataIssue } from "../helpers/data-issue";
import type { WidgetDataIssue, WidgetDataNotice } from "../helpers/data-issue";
import { WidgetState } from "./WidgetState";

export interface WidgetIssueStateProps {
  /** The issue to show; defaults to the one the host provides. */
  issue?: WidgetDataIssue | null;
  /** Or an error, classified the same way (scope, unavailable, failure). */
  error?: Error | null;
  retry?: () => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * The one representation of a widget without data to draw. The `Widget` frame
 * renders it automatically from the host's {@link WidgetDataIssueContext}; a
 * widget only renders it itself when it draws its body outside the frame.
 */
export function WidgetIssueState({ issue, error, retry, className, style }: WidgetIssueStateProps) {
  const provided = useContext(WidgetDataIssueContext).issue;
  const shown = issue ?? (error ? toDataIssue(error, retry ?? provided?.retry) : provided);
  if (!shown) return null;
  const onRetry = shown.retry ?? retry;
  if (shown.kind === "error") {
    return (
      <WidgetState
        tone="error"
        title={WIDGET_ISSUE_TEXT.error}
        description={WIDGET_ISSUE_TEXT.errorReason}
        className={className}
        style={style}
        action={
          onRetry ? (
            <Button size="sm" variant="secondary" disabled={shown.retrying} onClick={onRetry}>
              {shown.retrying ? WIDGET_ISSUE_TEXT.retrying : WIDGET_ISSUE_TEXT.retry}
            </Button>
          ) : null
        }
      />
    );
  }
  return (
    <WidgetState
      title={WIDGET_ISSUE_TEXT[shown.kind]}
      description={shown.reason}
      className={className}
      style={style}
    />
  );
}

/**
 * Header notice for results kept on screen after a failed update: one short
 * word group and a Retry, so the body keeps its size and the header its line.
 */
export function WidgetDataNoticeBadge({ notice }: { notice: WidgetDataNotice }) {
  const timeFormat = createDateFormatter(useDisplayTimezone(), {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const detail =
    notice.kind === "partial" ? WIDGET_ISSUE_TEXT.partialDetail : WIDGET_ISSUE_TEXT.staleDetail;
  const received =
    notice.updatedAt && notice.updatedAt > 0
      ? ` Last received ${timeFormat.format(new Date(notice.updatedAt))}.`
      : "";
  return (
    <span
      role="status"
      aria-live="polite"
      data-widget-notice={notice.kind}
      className="inline-flex max-w-full items-center gap-1 text-xs font-medium text-status-warn-text"
      title={`${detail}${received}`}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      <span className="truncate">
        {notice.kind === "partial" ? WIDGET_ISSUE_TEXT.partial : WIDGET_ISSUE_TEXT.stale}
      </span>
      <span className="sr-only">
        {detail}
        {received}
      </span>
      {notice.retry ? (
        // Inline text control: the header keeps its line height.
        <button
          type="button"
          disabled={notice.retrying}
          onClick={notice.retry}
          className="ml-1 rounded-control px-1 text-xs font-medium text-secondary underline-offset-2 hover:text-primary hover:underline focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none disabled:opacity-60"
        >
          {notice.retrying ? WIDGET_ISSUE_TEXT.retrying : WIDGET_ISSUE_TEXT.retry}
        </button>
      ) : null}
    </span>
  );
}
